import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Stack,
    Alert,
    Divider,
    FormControlLabel,
    Switch,
    useTheme,
    alpha
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { supabase } from '../../../../../supabaseClient';

export default function IssueTrigger({ batchLogId, createBatchLog, onDeviationCreated, showSnackbar, canEdit = false, batchTimeFormat = '12hour' }) {
    const theme = useTheme();
    const [issueDialogOpen, setIssueDialogOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        issue_type: '',
        issue_description: '',
        issue_severity: 'medium',
        flagged_by_role: 'operator',
        shift: dayjs(),
        equipment_used: '',
        temperature_actual: '',
        temperature_target: ''
    });

    const issueTypes = [
        { value: 'viscosity_out_of_spec', label: 'Viscosity Out of Spec' },
        { value: 'color_variation', label: 'Color Variation' },
        { value: 'missing_coa', label: 'Missing/Invalid COA' },
        { value: 'wrong_material', label: 'Wrong Material Added' },
        { value: 'temperature_deviation', label: 'Temperature Deviation' },
        { value: 'contamination', label: 'Contamination Detected' },
        { value: 'equipment_failure', label: 'Equipment Failure' },
        { value: 'other', label: 'Other Issue' }
    ];

    const severityLevels = [
        { value: 'low', label: 'Low - Minor issue' },
        { value: 'medium', label: 'Medium - Moderate problem' },
        { value: 'high', label: 'High - Serious issue' },
        { value: 'critical', label: 'Critical - Immediate action needed' }
    ];

    const handleSaveIssue = async () => {
        setLoading(true);
        try {
            const logId = await createBatchLog();
            if (!logId) {
                console.error('Failed to create or get batch log ID for deviation');
                showSnackbar?.('Failed to create batch log', 'error');
                setLoading(false);
                return;
            }

            const { data: { user } } = await supabase.auth.getUser();

            // Complete data object for batch_log_deviations table
            const deviationData = {
                batch_log_id: logId,
                issue_type: formData.issue_type,
                issue_description: formData.issue_description,
                issue_severity: formData.issue_severity,
                flagged_by_user_id: user?.id,
                flagged_by_role: formData.flagged_by_role,
                flagged_at: formData.shift.toISOString(),
                shift: formData.shift.toISOString(),
                equipment_used: formData.equipment_used,
                temperature_actual: formData.temperature_actual ? parseFloat(formData.temperature_actual) : null,
                temperature_target: formData.temperature_target ? parseFloat(formData.temperature_target) : null,
                status: 'open',
                detected_by: user?.email || 'Unknown User',
                // Legacy fields for backward compatibility
                deviation_type: formData.issue_type,
                severity_level: formData.issue_severity === 'low' ? 'Low' : 
                               formData.issue_severity === 'medium' ? 'Minor' :
                               formData.issue_severity === 'high' ? 'Major' : 'Critical',
                description: formData.issue_description,
                occurred_at: formData.shift.toISOString()
            };

            // Save to batch_log_deviations table
            const { data: deviation, error } = await supabase
                .from('batch_log_deviations')
                .insert(deviationData)
                .select()
                .single();

            if (error) {
                console.error('Error creating deviation:', error);
                showSnackbar?.('Error creating deviation: ' + error.message, 'error');
                setLoading(false);
                return;
            }

            // Reset form
            setFormData({
                issue_type: '',
                issue_description: '',
                issue_severity: 'medium',
                flagged_by_role: 'operator',
                shift: dayjs(),
                equipment_used: '',
                temperature_actual: '',
                temperature_target: ''
            });
            setIssueDialogOpen(false);
            
            if (onDeviationCreated) {
                onDeviationCreated(deviation);
            }

            showSnackbar?.('Issue reported successfully', 'success');

        } catch (error) {
            console.error('Error saving issue:', error);
            showSnackbar?.('Error saving issue: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box>
                <Grid container spacing={4}>
                    {/* Main Action Card */}
                    <Grid item xs={12}>
                        <Card sx={{ 
                            borderRadius: 4,
                            border: `1px solid ${theme.palette.grey[400]}`,
                            background: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 4, textAlign: 'center' }}>
                                <Typography variant="h5" sx={{ 
                                    fontWeight: 500, 
                                    color: theme.palette.error.main,
                                    mb: 2
                                }}>
                                    Report Production Issue
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                    color: theme.palette.text.secondary,
                                    fontWeight: 500,
                                    mb: 4
                                }}>
                                    Flag any problems immediately to maintain quality standards
                                </Typography>
                                
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => setIssueDialogOpen(true)}
                                    disabled={!canEdit}
                                    sx={{
                                        borderRadius: 3,
                                        px: 4,
                                        py: 1.5,
                                        fontSize: '1rem',
                                        fontWeight: 500,
                                        backgroundColor: canEdit ? theme.palette.error.main : theme.palette.grey[400],
                                        color: canEdit ? 'white' : theme.palette.grey[100],
                                        boxShadow: canEdit ? `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}` : 'none',
                                        '&:hover': {
                                            backgroundColor: canEdit ? theme.palette.error.main : theme.palette.grey[400],
                                            transform: canEdit ? 'translateY(-1px)' : 'none',
                                            boxShadow: canEdit ? `0 6px 20px ${alpha(theme.palette.error.main, 0.4)}` : 'none'
                                        },
                                        '&:disabled': {
                                            backgroundColor: theme.palette.grey[400],
                                            color: theme.palette.grey[100]
                                        },
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                    }}
                                >
                                    {canEdit ? 'Report Issue Now' : 'Read Only - No Edit Permissions'}
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Issue Reporting Dialog */}
                <Dialog 
                    open={issueDialogOpen} 
                    onClose={() => setIssueDialogOpen(false)}
                    maxWidth="lg"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 3,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                            maxHeight: '90vh',
                            overflow: 'hidden'
                        }
                    }}
                >
                    <DialogTitle sx={{ 
                        fontWeight: 500,
                        fontSize: '1.2rem',
                        color: theme.palette.error.main,
                        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: alpha(theme.palette.error.main, 0.05),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 3
                    }}>
                        Report Production Issue
                    </DialogTitle>
                    
                    <DialogContent sx={{ 
                        backgroundColor: alpha(theme.palette.grey[50], 0.5), 
                        p: 3,
                        maxHeight: '80vh', 
                        overflowY: 'auto',
                        backdropFilter: 'blur(10px)',
                        mt: 3
                    }}>
                        <Grid container spacing={3}>
                            {/* Basic Information */}
                            <Grid item xs={12}>
                                <Card sx={{ 
                                    p: 3, 
                                    mt: 1,
                                    border: `1px solid ${theme.palette.grey[400]}`, 
                                    borderRadius: 3, 
                                    backgroundColor: 'white',
                                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                    }
                                }}>
                                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: theme.palette.text.primary }}>
                                        Basic Information
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Type of Issue</InputLabel>
                                                <Select
                                                    value={formData.issue_type}
                                                    onChange={(e) => setFormData({...formData, issue_type: e.target.value})}
                                                    label="Type of Issue"
                                                    disabled={!canEdit}
                                                >
                                                    {issueTypes.map(type => (
                                                        <MenuItem key={type.value} value={type.value}>
                                                            {type.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Severity Level</InputLabel>
                                                <Select
                                                    value={formData.issue_severity}
                                                    onChange={(e) => setFormData({...formData, issue_severity: e.target.value})}
                                                    label="Severity Level"
                                                    disabled={!canEdit}
                                                >
                                                    {severityLevels.map(level => (
                                                        <MenuItem key={level.value} value={level.value}>
                                                            {level.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Detailed Description"
                                                value={formData.issue_description}
                                                onChange={(e) => setFormData({...formData, issue_description: e.target.value})}
                                                placeholder={canEdit ? "Describe the issue in detail..." : "Issue description (read-only)"}
                                                disabled={!canEdit}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>

                            {/* Context Information */}
                            <Grid item xs={12}>
                                <Card sx={{ 
                                    p: 3, 
                                    mt: 1,
                                    border: `1px solid ${theme.palette.grey[400]}`, 
                                    borderRadius: 3, 
                                    backgroundColor: 'white',
                                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                    }
                                }}>
                                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: theme.palette.text.primary }}>
                                        Context Information
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                            <FormControl fullWidth>
                                                <InputLabel>Your Role</InputLabel>
                                                <Select
                                                    value={formData.flagged_by_role}
                                                    onChange={(e) => setFormData({...formData, flagged_by_role: e.target.value})}
                                                    label="Your Role"
                                                    disabled={!canEdit}
                                                >
                                                    <MenuItem value="operator">Operator</MenuItem>
                                                    <MenuItem value="qa_staff">QA Staff</MenuItem>
                                                    <MenuItem value="supervisor">Supervisor</MenuItem>
                                                    <MenuItem value="qa_manager">QA Manager</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12} md={4}>
                                            <DateTimePicker
                                                label="Shift Date & Time"
                                                value={formData.shift}
                                                onChange={(newValue) => setFormData({...formData, shift: newValue})}
                                                disabled={!canEdit}
                                                format={batchTimeFormat === '24hour' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY h:mm A'}
                                                ampm={batchTimeFormat !== '24hour'}
                                                slotProps={{
                                                    textField: {
                                                        fullWidth: true,
                                                        variant: 'outlined'
                                                    }
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="Equipment Used"
                                                value={formData.equipment_used}
                                                onChange={(e) => setFormData({...formData, equipment_used: e.target.value})}
                                                disabled={!canEdit}
                                            />
                                        </Grid>

                                        {/* Temperature fields for temperature deviations */}
                                        {formData.issue_type === 'temperature_deviation' && (
                                            <>
                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Target Temperature (°C)"
                                                        type="number"
                                                        value={formData.temperature_target}
                                                        onChange={(e) => setFormData({...formData, temperature_target: e.target.value})}
                                                        disabled={!canEdit}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Actual Temperature (°C)"
                                                        type="number"
                                                        value={formData.temperature_actual}
                                                        onChange={(e) => setFormData({...formData, temperature_actual: e.target.value})}
                                                        disabled={!canEdit}
                                                    />
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                </Card>
                            </Grid>


                        </Grid>
                    </DialogContent>
                    
                    <DialogActions sx={{ 
                        p: 3, 
                        gap: 2, 
                        borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
                    }}>
                        <Button 
                            onClick={() => setIssueDialogOpen(false)}
                            sx={{
                                borderRadius: 2,
                                px: 3,
                                py: 1.5,
                                color: theme.palette.text.secondary,
                                fontWeight: 500,
                                background: 'rgba(255, 255, 255, 0.8)',
                                backdropFilter: 'blur(10px)',
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.04)
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="contained"
                            onClick={handleSaveIssue}
                            disabled={!canEdit || loading || !formData.issue_type || !formData.issue_description}
                            sx={{
                                borderRadius: 2,
                                px: 4,
                                py: 1.5,
                                backgroundColor: canEdit ? theme.palette.error.main : theme.palette.grey[400],
                                color: canEdit ? 'white' : theme.palette.grey[100],
                                fontWeight: 500,
                                boxShadow: canEdit ? `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}` : 'none',
                                '&:hover': {
                                    backgroundColor: canEdit ? theme.palette.error.main : theme.palette.grey[400],
                                    boxShadow: canEdit ? `0 6px 20px ${alpha(theme.palette.error.main, 0.4)}` : 'none'
                                },
                                '&:disabled': {
                                    background: theme.palette.grey[400],
                                    color: theme.palette.grey[100]
                                }
                            }}
                        >
                            {!canEdit ? 'Read Only - No Edit Permissions' : (loading ? 'Reporting...' : 'Report Issue')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </LocalizationProvider>
    );
}